import { Grid, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { INTEGRATION_PROVIDERS, type IntegrationProviders } from '@vy/types';
import type { FC } from 'react';
import { useIntegrationContextProvider } from '../../hooks/use-integration-context-provider';
import type { IntegrationProviderProperties } from '../../integrations-page-types/integration-provider-properties';
import { INTEGRATIONS_TYPES } from '../../integrations-page-types/integrations-types';
import { IntegrationCard } from '../IntegrationCard/IntegrationCard';
import netSuiteSuiteAppBadge from '../../../../../assets/images/net-suite-suiteapp-badge.png';

type IntegrationCardGroupProps = {
  sectionTitle: string;
  connected: boolean;
  integrations: IntegrationProviders[];
};

export const IntegrationCardGroup: FC<IntegrationCardGroupProps> = ({
  sectionTitle,
  integrations,
  connected,
}) => {
  const { setSelectedIntegration } = useIntegrationContextProvider();
  const integrationPropertiesByProvider = getIntegrationPropertiesByProvider();

  return (
    <Stack
      sx={{
        gap: 2,
        height: "100%"
      }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontSize: "16px",
          fontWeight: 500
        }}>
        {sectionTitle}
      </Typography>
      <Grid container spacing={3} sx={{
        width: "100%"
      }}>
        {integrations.map((integrationProvider) => {
          const { integrationType, description, supported, logo, imageLogo } =
            integrationPropertiesByProvider[integrationProvider];

          return (
            <Grid
              key={integrationProvider}
              size={{
                xs: 12,
                sm: 6,
                md: 4
              }}>
              <IntegrationCard
                title={integrationProvider}
                serviceType={integrationType}
                description={description}
                supported={supported}
                logo={logo}
                imageLogo={imageLogo}
                isConnected={connected}
                onClick={() => setSelectedIntegration(integrationProvider)}
              />
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};

const getIntegrationPropertiesByProvider = (): Record<
  IntegrationProviders,
  IntegrationProviderProperties
> => {
  return {
    [INTEGRATION_PROVIDERS.vayu]: {
      logo: (
        <VyIcon
          icon="LogoSymbol"
          style={{
            transform: 'scale(1.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Api,
      description: 'Manage Vayu API tokens.',
    },
    [INTEGRATION_PROVIDERS.netSuite]: {
      logo: (
        <img
          src={netSuiteSuiteAppBadge}
          alt="Built for Oracle NetSuite — Hybrid SuiteApp"
          style={{ height: 54, width: 'auto', display: 'block', borderRadius: '8px' }}
        />
      ),
      imageLogo: true,
      supported: true,
      integrationType: INTEGRATIONS_TYPES.ERP,
      description: 'Centralize business processes with an integrated ERP solution.',
    },
    [INTEGRATION_PROVIDERS.stripe]: {
      logo: (
        <VyIcon
          icon="StripeLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Payment,
      description: 'Simplify online payments and subscription billing.',
    },
    [INTEGRATION_PROVIDERS.hubspot]: {
      logo: (
        <VyIcon
          icon="HubspotLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.CRM,
      description: 'Streamline customer relationship management and marketing automation.',
    },
    [INTEGRATION_PROVIDERS.salesforce]: {
      logo: (
        <VyIcon
          icon="SalesforceLogoIcon"
          style={{
            transform: 'scale(0.2)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.CRM,
      description: 'Manage sales, service, and marketing in one powerful CRM.',
    },
    [INTEGRATION_PROVIDERS.quickBooks]: {
      logo: (
        <VyIcon
          icon="QuickbooksLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.ERP,
      description: 'Manage accounting, payroll, and finances seamlessly.',
    },
    [INTEGRATION_PROVIDERS.slack]: {
      logo: (
        <VyIcon
          icon="SlackLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Communication,
      description: 'Enhance team communication and collaboration effortlessly.',
    },
    [INTEGRATION_PROVIDERS.S3]: {
      logo: (
        <VyIcon
          icon="AwsLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Storage,
      description: 'Securely store and retrieve data at any scale with cloud storage.',
    },
    [INTEGRATION_PROVIDERS.morning]: {
      logo: <VyIcon icon="MorningIcon" style={{ transform: 'scale(0.225)' }} />,
      supported: true,
      integrationType: INTEGRATIONS_TYPES.ERP,
      description: 'Manage accounting and business operations seamlessly.',
    },
    [INTEGRATION_PROVIDERS.anrok]: {
      logo: (
        <VyIcon
          icon="AnrokIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Tax,
      description: 'Simplify online tax calculations and compliance.',
    },
    [INTEGRATION_PROVIDERS.snowflake]: {
      logo: (
        <VyIcon
          icon="SnowflakeLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Storage,
      description: 'Import events and data to Snowflake data warehouse.',
    },
    [INTEGRATION_PROVIDERS.connact]: {
      logo: (
        <VyIcon
          icon="Integration"
          style={{ transform: 'scale(0.225)' }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Marketplace,
      description: 'PLACEHOLDER DESCRIPTION',
    },
    [INTEGRATION_PROVIDERS.xero]: {
      logo: (
        <VyIcon
          icon="XeroLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.ERP,
      description: 'Manage accounting, payroll, and finances with cloud-based ERP.',
    },
    [INTEGRATION_PROVIDERS.paddle]: {
      logo: (
        <VyIcon
          icon="PaddleLogoIcon"
          style={{
            transform: 'scale(0.225)',
          }}
        />
      ),
      supported: true,
      integrationType: INTEGRATIONS_TYPES.Payment,
      description: 'Simplify subscription billing and payment processing.',
    },
  };
};
